import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import type { SubmissionDisplayState } from '../../../lib/submissionDisplayState';
import type { SubmissionQueueState } from './SubmissionStatusChip';
import { SubmissionStatusChip } from './SubmissionStatusChip';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export type GradeQueueItemProps = {
  studentName: string;
  secondaryLine: string;
  state: SubmissionQueueState;
  displayState?: SubmissionDisplayState;
  synced: boolean;
  score: number;
  maxScore: number;
  focused: boolean;
  /** Bulk queue selection (sync / delete) */
  bulkSelected?: boolean;
  onToggleBulk?: () => void;
  onSelect: () => void;
};

export function GradeQueueItem({
  studentName,
  secondaryLine,
  state,
  displayState,
  synced,
  score,
  maxScore,
  focused,
  bulkSelected = false,
  onToggleBulk,
  onSelect,
}: GradeQueueItemProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[60px] gap-2">
      {onToggleBulk ? (
        <button
          type="button"
          aria-label={t('audit.studio.toggleSelect')}
          aria-pressed={bulkSelected}
          onClick={(e) => {
            e.stopPropagation();
            onToggleBulk();
          }}
          className={cn(
            'mt-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-dg-control border text-xs font-bold transition-colors',
            bulkSelected
              ? 'border-dg-accent-grade bg-dg-accent-grade text-white'
              : 'border-slate-300 bg-white text-slate-500 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-300'
          )}
        >
          {bulkSelected ? '✓' : ''}
        </button>
      ) : null}
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'min-w-0 flex-1 rounded-[12px] border p-3 text-left transition-all duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dg-accent-grade/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
          focused
            ? 'border-dg-accent-grade/50 bg-dg-accent-gradeLight/90 shadow-sm ring-2 ring-dg-accent-grade/35 dark:border-amber-600/40 dark:bg-amber-950/35 dark:ring-amber-500/30'
            : 'border-slate-200/80 bg-white hover:border-slate-300/90 hover:bg-slate-50/90 dark:border-slate-700/70 dark:bg-slate-900/50 dark:hover:border-slate-600 dark:hover:bg-slate-800/50',
          bulkSelected && !focused && 'border-indigo-200/80 dark:border-indigo-500/30'
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
              focused
                ? 'bg-dg-accent-grade text-white shadow-sm'
                : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
            )}
            aria-hidden
          >
            {initials(studentName)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-dg-body font-semibold text-slate-900 dark:text-white">
              {studentName}
            </div>
            <div className="mt-0.5 truncate text-dg-secondary text-slate-600 dark:text-slate-400">
              {secondaryLine}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <SubmissionStatusChip state={state} displayState={displayState} synced={synced} />
              <span className="text-dg-secondary font-medium tabular-nums text-slate-700 dark:text-slate-300">
                {score}/{maxScore}
              </span>
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}
